import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
} from '@mui/material';
import {
  BarChart,
  EmojiEvents,
  TrendingUp,
} from '@mui/icons-material';
import { useTournaments, useTournamentLeaderboard } from '@/hooks/useApi';
import { SportFilter, LoadingState } from '@/components';
import type { SportType } from '@/types';

type AnalyticsTab = 'leaderboards' | 'trends' | 'history';

const getMetricName = (metric: string): string => {
  const names: Record<string, string> = {
    goals: 'Голы',
    assists: 'Ассисты',
    points: 'Очки',
    saves: 'Сейвы',
    rebounds: 'Подборы',
    blocks: 'Блоки',
    steals: 'Перехваты',
    shots: 'Удары',
    minutesPlayed: 'Минуты',
    matchesPlayed: 'Матчи',
    eloDelta: 'ELO',
  };
  return names[metric] || metric;
};

export const AnalyticsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<AnalyticsTab>('leaderboards');
  const [selectedSport, setSelectedSport] = useState<SportType | null>(null);
  const [selectedTournament, setSelectedTournament] = useState<string | null>(null);
  const [selectedMetric, setSelectedMetric] = useState<string>('goals');

  const { data: tournaments, isLoading: tournamentsLoading } = useTournaments({
    limit: 20,
    sportType: selectedSport || undefined,
  });

  const { data: leaderboard, isLoading: leaderboardLoading } = useTournamentLeaderboard(
    selectedTournament || '',
    { metric: selectedMetric, limit: 20 },
    { enabled: !!selectedTournament }
  );

  const handleTabChange = (_: React.SyntheticEvent, newValue: AnalyticsTab) => {
    setActiveTab(newValue);
  };

  return (
    <Box sx={{ pb: 10 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 2,
        }}
      >
        <BarChart color="primary" sx={{ fontSize: 28 }} />
        <Typography variant="h5" fontWeight="bold">
          Аналитика
        </Typography>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
        >
          <Tab
            value="leaderboards"
            icon={<EmojiEvents fontSize="small" />}
            iconPosition="start"
            label="Лидерборды"
            sx={{ minHeight: 48, textTransform: 'none' }}
          />
          <Tab
            value="trends"
            icon={<TrendingUp fontSize="small" />}
            iconPosition="start"
            label="Тренды"
            sx={{ minHeight: 48, textTransform: 'none' }}
          />
          <Tab
            value="history"
            icon={<BarChart fontSize="small" />}
            iconPosition="start"
            label="История"
            sx={{ minHeight: 48, textTransform: 'none' }}
          />
        </Tabs>
      </Box>

      {/* Content */}
      <Box sx={{ p: 1 }}>
        {activeTab === 'leaderboards' && (
          <>
            {/* Sport Filter */}
            <SportFilter selectedSport={selectedSport} onSelectSport={setSelectedSport} />

            {/* Tournament Selection */}
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Выберите турнир
              </Typography>
              {tournamentsLoading ? (
                <LoadingState message="Загрузка турниров..." />
              ) : (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {tournaments?.data.map((tournament) => (
                    <Chip
                      key={tournament.id}
                      label={tournament.name}
                      onClick={() => setSelectedTournament(tournament.id)}
                      color={selectedTournament === tournament.id ? 'primary' : 'default'}
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Box>
              )}
            </Paper>

            {/* Metric Selection */}
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Метрика
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {['goals', 'assists', 'points', 'saves', 'matchesPlayed'].map((metric) => (
                  <Chip
                    key={metric}
                    label={getMetricName(metric)}
                    onClick={() => setSelectedMetric(metric)}
                    color={selectedMetric === metric ? 'secondary' : 'default'}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
            </Paper>

            {/* Leaderboard */}
            {selectedTournament && (
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {getMetricName(selectedMetric)} - Топ игроков
                </Typography>
                {leaderboardLoading ? (
                  <LoadingState message="Загрузка лидерборда..." />
                ) : leaderboard?.entries.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                    Нет данных
                  </Typography>
                ) : (
                  <List sx={{ p: 0 }}>
                    {leaderboard?.entries.map((player, index) => (
                      <ListItem
                        key={player.playerId}
                        sx={{
                          bgcolor: index < 3 ? 'action.hover' : 'transparent',
                          borderRadius: 1,
                          mb: 0.5,
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar
                            sx={{
                              bgcolor:
                                index === 0
                                  ? '#ffd700'
                                  : index === 1
                                  ? '#c0c0c0'
                                  : index === 2
                                  ? '#cd7f32'
                                  : 'primary.main',
                              color: index < 3 ? 'black' : 'white',
                              fontWeight: 'bold',
                            }}
                          >
                            {index + 1}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Typography variant="body1" fontWeight={index < 3 ? 'bold' : 'normal'}>
                              {player.playerName}
                            </Typography>
                          }
                          secondary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {player.teamName && (
                                <Typography variant="caption" color="text.secondary">
                                  {player.teamName}
                                </Typography>
                              )}
                              <Typography variant="caption" color="primary">
                                {player.matchesPlayed} матчей
                              </Typography>
                            </Box>
                          }
                        />
                        <Chip
                          label={
                            selectedMetric === 'goals'
                              ? player.goals
                              : selectedMetric === 'assists'
                              ? player.assists
                              : selectedMetric === 'points'
                              ? player.points
                              : selectedMetric === 'saves'
                              ? player.saves
                              : player.matchesPlayed
                          }
                          color="primary"
                          size="small"
                          sx={{ fontWeight: 'bold' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Paper>
            )}
          </>
        )}

        {activeTab === 'trends' && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <TrendingUp sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Тренды
            </Typography>
            <Typography variant="body2" color="text.disabled">
              Анализ трендов будет доступен скоро
            </Typography>
          </Paper>
        )}

        {activeTab === 'history' && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <BarChart sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              История
            </Typography>
            <Typography variant="body2" color="text.disabled">
              Исторические данные будут доступны скоро
            </Typography>
          </Paper>
        )}
      </Box>
    </Box>
  );
};
